<script setup lang="ts">
import { computed } from 'vue';
import { isNilOrBlankString, toKebabCase } from '@nova/core';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Number field type - numeric input
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  persistentPlaceholder = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: number | ''];
  /** @deprecated Use update:model-value instead */
  input: [value: number | ''];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  persistentPlaceholder,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

/**
 * A blank field emits '' rather than a number: Number('') and Number(null) are both 0, so a
 * cleared optional field would otherwise save as zero. Typing emits '' when the input empties,
 * while a parent v-form's reset() sets the model to null.
 */
function handleInput(value: string | null) {
  const numericValue = isNilOrBlankString(value) ? '' : Number(value);
  emit('update:model-value', numericValue);
  emit('input', numericValue);
}
</script>

<template>
  <custom-field-input-base
    :field="field"
    v-bind="normalizedProps"
    :data-testid="`${testId || toKebabCase(field.label)}-custom-field-input`">
    <v-text-field
      color="primary"
      density="comfortable"
      inputmode="numeric"
      type="number"
      variant="outlined"
      :hide-details="normalizedProps.hideDetails"
      :model-value="modelValue"
      :persistent-placeholder="normalizedProps.persistentPlaceholder"
      :placeholder="field.placeholder"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
